import { lazy } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { setupChunkErrorHandler, clearChunkReloadFlag } from 'sn.ticketing.frontends.shared/browser/chunk-error-handler';

import { initializeErrorTracking } from '@app/configuration';
import { errorTracker } from '@/shared/lib/error-tracking';
import { venueDataAccessor } from '@stores/webStorages/storages/VenueData.accessor';
import { apiConfigAccessor } from '@stores/webStorages/storages/ApiConfig.accessor';
import { initSanitizer } from '@lib/html/sanitizer';

import { insertThirdPartyScripts } from './features/third-party-scripts/lib';

const root = createRoot(document.getElementById('root')!, {
  onUncaughtError: Sentry.reactErrorHandler(),
  // todo: uncomment after navigation error catch remove
  // onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError(error, errorInfo) {
    Sentry.captureException(error, { level: 'warning', extra: { errorInfo } });
  },
});

import './index.css';

const _polyfilInternals = () => {
  if (!('crypto' in window)) {
    Object.defineProperty(window, 'crypto', {
      value: { randomUUID: () => Math.random().toString() },
    });
  } else if (!('randomUUID' in window.crypto)) {
    // window.crypto is non-configurable on iOS Safari — redefining it throws
    // "TypeError: Attempting to change value of a readonly property".
    // Patch only randomUUID onto the existing object instead.
    Object.defineProperty(window.crypto, 'randomUUID', {
      value: () => Math.random().toString() as ReturnType<typeof crypto.randomUUID>,
      writable: true,
      configurable: true,
    });
  }
};

/**
 * React app init
 */
const _loadAppModule = () => {
  const App = lazy(() =>
    import('./app/App').then((mod) => {
      // The entry App chunk loaded fine — give this session its one-reload
      // budget back so a *later* stale chunk (e.g. across a subsequent deploy
      // within a long funnel session) can auto-heal instead of dead-ending on
      // the fallback screen. Cleared only on success, never mid-recovery.
      clearChunkReloadFlag();
      return mod;
    }),
  );
  root.render(<App />);
};

/**
 * Function that binds specific SNDomain to the web storages in order to decouple usage of a window.SNDomain
 */
const _bindDomain = () => {
  venueDataAccessor.setVenueData(window.SNDomain.detectVenue());
  apiConfigAccessor.setConfig(window.SNDomain.appConfig);
};

const init = async () => {
  try {
    setupChunkErrorHandler((msg, extra) =>
      Sentry.captureMessage(msg, {
        level: 'warning',
        // Tag so the dashboard can isolate genuinely-stuck sessions
        // (alreadyReloaded=true) from anything else. SN-8114.
        tags: { alreadyReloaded: extra?.alreadyReloaded ?? false, staleChunkTrigger: extra?.trigger },
        extra,
      }),
    );
    await window.SNDomain.loadingSignal;
    _polyfilInternals();
    _bindDomain();
    initializeErrorTracking();
    insertThirdPartyScripts();
    // Prime the DOMPurify chunk in parallel with the App chunk so render-time
    // sanitize() calls are synchronous by the time they run.
    void initSanitizer();
    _loadAppModule();
  } catch (error) {
    errorTracker.captureException(error, {
      extra: { type: 'initialization_error' },
    });
    root.render(<p>No data</p>);
  }
};

init();
// force-rehash
